import type { OrderStatusEnum } from '@@fire-b2b/graphql/codegen/graphql'
import type { HexColorString } from '@@fire-base/types/utils/ColorString'
// FIXME(corsin) remove optionals since defaults where set
export type B2BThemeName = 'light' | 'dark'
export type B2BThemeEffect = 'none' | 'branded'
export type B2BLookImageGallery = 'overview_image' | 'left_screen' | 'right_screen'

export type B2BConfiguration = {
  login: B2BConfigurationLogin
  contact: B2BConfigurationContact
  templates: Record<string, B2BConfigurationTemplate[]>
  price: B2BConfigurationPrice
  product: B2BConfigurationProduct
  productInfoList: B2BConfigurationProductInfoList[]
  productInfoTabs: B2BConfigurationProductInfoTab[]
  productRelations: B2BConfigurationProductRelation[]
  catalog: B2BConfigurationCatalog
  cartPosition: B2BConfigurationCartPosition
  cart: B2BConfigurationCart
  wishlist: B2BConfigurationWishlist
  look: B2BConfigurationLook
  themeworld: B2BConfigurationThemeworld
  blog: B2BConfigurationBlog
  team: B2BConfigurationTeam
  legal: B2BConfigurationLegal
  form: B2BConfigurationForm
  supportCenter: B2BConfigurationSupportCenter
  order: B2BConfigurationOrder
  users: B2BConfigurationUsers
  apiUsers: B2BConfigurationApiUsers
  addresses: B2BConfigurationAddresses
  apiAddresses: B2BConfigurationApiAddresses
  landing: B2BConfigurationLanding
  elements: B2BConfigurationElements
  error: B2BConfigurationError
  openOrder: B2BConfigurationOpenOrder
}

export type B2BConfigurationOpenOrder = {
  hasPositionDelete: boolean
}

export type B2BConfigurationLogin = {
  useRegisterForm: boolean
  useSsoSaml: boolean
  useSsoGoogle: boolean
  hideFireBranding: boolean
}

export type B2BConfigurationContact = {
  defaultLeftTeamId?: number
  defaultRightTeamId?: number
  leftCard?: {
    showAlways?: boolean
    title?: Record<string, string>
    description?: Record<string, string>
    buttonLeftLabel?: Record<string, string>
    buttonLeftUrl?: Record<string, string>
    buttonRightLabel?: Record<string, string>
    buttonRightUrl?: Record<string, string>
  }
}

export type B2BConfigurationTemplate = {
  label: Record<string, string>
  icon: string
  path: string
}

export type B2BConfigurationPrice = {
  format?: string
  formatMapping?: Record<string, string>
  showRRP?: boolean
}

export type B2BConfigurationProductTemplateItem = 'details' | 'themeworlds' | 'looks' | 'relatedProducts'
export type B2BConfigurationProduct = {
  useTemplate?: boolean
  showVideo?: boolean
  allowWishlist?: boolean
  showDescription?: boolean
  bulletpoints?: string[]
  tagStyle?: B2BConfigurationTagStyle[]
  template?: B2BConfigurationProductTemplateItem[]
}
export type B2BConfigurationTagStyle = {
  /**
   * Tag ID which will receive this hatched pattern style.
   */
  id: number
  /**
   * Hatched pattern background color
   */
  color1: HexColorString
  /**
   * Hatched pattern background color on hover
   */
  color1Hover?: HexColorString
  /**
   * Hatched pattern line color
   */
  color2: HexColorString
}

export interface B2BConfigurationProductInfoList {
  value: string
  status?: boolean
  hideIfEmpty?: boolean
  label: LocalizedString
  icon?: string
}

export type B2BConfigurationProductInfoTab = {
  type: 'custom' | 'sizes'
  label: Record<string, string>
  status?: boolean
  children: {
    value: string
    status?: boolean
    hideIfEmpty?: boolean
    label: Record<string, string>
  }[]
}

export enum B2BConfigurationProductRelationType {
  COLOR = 'color',
  THEMEWORLD = 'themeworld',
  LOOK = 'look',
  FEATURE = 'feature',
  ITEM = 'item',
}

export type B2BConfigurationProductRelation = {
  type: B2BConfigurationProductRelationType
  id: string
  status?: boolean
  label?: LocalizedString
  icon?: string
}

export type B2BConfigurationCatalog = {
  useTemplate?: boolean
  hasOnlyMainColor?: boolean
  hasListView?: boolean
  hasGridView?: boolean
  hasSizes?: boolean
  useListView?: boolean
  useListViewMobile?: boolean
  useOnlyMainColor?: boolean
}

export type B2BConfigurationCartPosition = {
  hasCommission?: boolean
  hasWishlistDate?: boolean
  hasExceptionDate?: boolean
  hasComment?: boolean
  hasPriceTagRRP?: boolean
}

export type B2BConfigurationCart = {
  showProductInfoList?: boolean
  reorderForm?: string
}

export type B2BConfigurationWishlist = {
  hasPrivateLists?: boolean
}

export type B2BConfigurationLook = {
  mainTitle: LocalizedString
  imageGallery?: B2BLookImageGallery[]
  useModule?: boolean
  useTemplate?: boolean
  showVideo?: boolean
  showTitleInSlider?: boolean
  useCustomTitle?: boolean
  categoryTitleUppercase?: boolean
  theme?: B2BThemeName
  effect?: B2BThemeEffect
}

export type B2BConfigurationLandingTemplateItem = 'heroBanner' | 'themeworlds' | 'news' | 'looks'

export type B2BConfigurationLanding = {
  useTemplate?: boolean
  preOrderTemplate?: B2BConfigurationLandingTemplateItem[]
  reOrderTemplate?: B2BConfigurationLandingTemplateItem[]
}

export type B2BConfigurationThemeworldTemplateItem =
  | 'hero'
  | 'keyPieces'
  | 'story'
  | 'video'
  | 'products'
  | 'relatedThemeworlds'
export type B2BConfigurationThemeworld = {
  mainTitle?: LocalizedString
  useModule?: boolean
  useTemplate?: boolean
  template?: B2BConfigurationThemeworldTemplateItem[]
  showVideo?: boolean
  categoryTitleUppercase?: boolean
  theme: B2BThemeName
  effect: B2BThemeEffect
}

export type B2BConfigurationBlog = {
  useModule?: boolean
  useTemplate?: boolean
}

export type B2BConfigurationTeam = {
  useCategoryIcons?: boolean
}

export type B2BConfigurationLegal = {
  hasImprint: boolean
  hasTermsOfService: boolean
  hasPrivacyPolicy: boolean
}

export type B2BConfigurationForm = {
  list?: {
    ident: string
    icon: string
  }[]
}

export type B2BConfigurationSupportCenter = {
  useModule?: boolean
  useTemplate?: boolean
}

export type B2BConfigurationOrder = {
  altOrderStatus?: Partial<
    Record<
      OrderStatusEnum,
      {
        label?: LocalizedString
      }
    >
  >
}

export type B2BConfigurationUsers = {
  hasCreateForm?: boolean
  hasContactForm?: boolean
  rightAdmin?: boolean
  rightManageUsers?: boolean
  rightViewOrders?: boolean
  rightViewDocuments?: boolean
  rightViewContracts?: boolean
  rightPlaceOrders?: boolean
}

export type B2BConfigurationApiUsers = {
  canDelete?: boolean
  canEdit?: boolean
  canEditEmail?: boolean
  canEditRights?: boolean
}

export type B2BConfigurationAddresses = {
  hasCreateForm?: boolean
  hasContactForm?: boolean
  canDelete?: boolean
  canEdit?: boolean
}

export type B2BConfigurationApiAddresses = {
  canDelete?: boolean
  canEdit?: boolean
}
export type B2BConfigurationElements = {
  lookCategoryItem?: {
    showNoOfItems?: boolean
  }
}

export type B2BConfigurationError = {
  theme?: 'light' | 'dark'
  effect?: 'none' | 'branded'
}

// TODO what to do with the default values? remove or all false / undefined ?!
export const DEFAULT_B2B_CONFIGURATION: B2BConfiguration = {
  login: {
    useRegisterForm: true,
    useSsoSaml: false,
    useSsoGoogle: false,
    hideFireBranding: false,
  },
  contact: {},
  templates: {},
  price: {},
  product: {},
  productInfoList: [],
  productInfoTabs: [],
  productRelations: [],
  catalog: {},
  cartPosition: {},
  cart: {},
  wishlist: {},
  look: {
    theme: 'light',
    effect: 'none',
    mainTitle: {
      en: 'Look',
      de: 'Look',
    },
    showTitleInSlider: false,
    useCustomTitle: false,
    imageGallery: ['overview_image', 'left_screen', 'right_screen'],
  },
  themeworld: {
    theme: 'light',
    effect: 'none',
  },
  blog: {},
  team: {
    useCategoryIcons: false,
  },
  legal: {
    hasImprint: true,
    hasTermsOfService: true,
    hasPrivacyPolicy: true,
  },
  form: {},
  supportCenter: {},
  order: {},
  users: {},
  apiUsers: {},
  addresses: {},
  apiAddresses: {},
  landing: {},
  elements: {
    lookCategoryItem: {
      showNoOfItems: true,
    },
  },
  error: {
    theme: 'light',
    effect: 'none',
  },
  openOrder: {
    hasPositionDelete: false,
  },
}
